/**
 * Flattens a CMM workflow document into the answer rows the MN judge expects.
 *
 * Mirrors the RPA's pass-1 payload: one row per question, with a free-text
 * elaboration following its parent as a separate row sharing the parent's
 * question text (the judge merges the pair before assessing).
 *
 * @module pharmaQuestionaire/utils/buildMnJudgeAnswers
 */
import { MnJudgeSubmittedAnswer } from "../../../api/postCall/postMnJudgeAssessment";
import {
  PharmaBranchedQuestionnaire,
  Question,
} from "../../../data-model/cmmMedicalNeccessityQuestionnaire";

/**
 * Collects the free-text elaborations a reviewer typed alongside a question's
 * selected choices.
 *
 * @param question - Question to read choices from
 * @returns Non-empty free-text answers, in choice order
 */
const getFreeTextAnswers = (question: Question): string[] =>
  (question.choices ?? [])
    .map((choice) => choice.additional_free_text_answer?.trim() ?? "")
    .filter((freeText) => freeText !== "");

/**
 * Builds the `answers` payload for POST `pharmacy/mn-judge/assess`.
 *
 * Unanswered rows are dropped — the judge ignores them anyway, and sending
 * them only inflates the prompt.
 *
 * @param questionnaire - Workflow document holding the reviewed answers
 * @returns Answer rows for the judge, empty when nothing is answered
 */
export const buildMnJudgeAnswers = (
  questionnaire: PharmaBranchedQuestionnaire | null,
): MnJudgeSubmittedAnswer[] => {
  const sections = [
    ...(questionnaire?.clinical_question_sections ?? []),
    ...(questionnaire?.special_question_sections ?? []),
  ];
  const questions = sections.flatMap((section) => section.questions ?? []);

  return questions.flatMap((question) => {
    const hasAnswer = Array.isArray(question.answer)
      ? question.answer.length > 0
      : (question.answer ?? "") !== "";
    if (!hasAnswer) return [];

    const answerRow: MnJudgeSubmittedAnswer = {
      question: question.text,
      answer: question.answer,
      question_name: question.name,
    };
    const freeTextRows = getFreeTextAnswers(question).map((freeText) => ({
      question: question.text,
      answer: freeText,
      question_name: question.name,
    }));
    return [answerRow, ...freeTextRows];
  });
};
